// ==============================================
// Auth Provider (Better Auth)
// ==============================================

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { authClient, signIn, signUp, signOut } from '../lib/auth-client';
import { api } from '../services/api';
import type { User } from '../types/auth';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  getAccessToken: () => Promise<string | null>;
  /** Save a per-user list page-size preference (optimistic + persisted). */
  setListPageSize: (listKey: string, size: number) => void;
  /**
   * Re-fetch the profile into context. Call after a server-side change to the
   * user (e.g. email change) — the session-change sync only runs on
   * login/logout, so context would otherwise stay stale until a full reload.
   */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  clearError: () => {},
  getAccessToken: async () => null,
  setListPageSize: () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const queryClient = useQueryClient();
  const [appUser, setAppUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The cache is cleared in login()/logout(), but a magic-link sign-in never
  // passes through either — the cookie session changes underneath a running
  // app. Anything still mounted keeps polling as the OLD user: admin pages
  // firing requireAdmin queries as the new non-admin threw a burst of
  // "Forbidden" toasts, and the cache still held the previous account's data.
  // So: when the session identity changes from one signed-in user to another,
  // drop everything.
  const prevUserIdRef = useRef<string | null>(null);

  // Sync app user profile whenever session changes
  useEffect(() => {
    if (sessionPending) return;

    const uid = session?.user?.id ?? null;
    if (uid !== null && prevUserIdRef.current !== null && uid !== prevUserIdRef.current) {
      queryClient.clear();
    }
    // Better Auth re-checks the session every time the tab regains focus and
    // hands back a new object for the same person. Showing the loading state
    // for that made AdminGuard swap every admin page for "Verifying access…",
    // which threw away open sections and scroll position. The same person is
    // refreshed quietly; only a new person (or a first load) shows it.
    const samePerson = uid !== null && uid === prevUserIdRef.current;
    prevUserIdRef.current = uid;

    if (session?.user) {
      if (!samePerson) setIsLoading(true);
      api.getProfile()
        .then((user) => setAppUser(user))
        // A quiet refresh that fails (a network blip as the tab comes back)
        // keeps the person signed in; a real sign-out arrives as no session.
        .catch(() => { if (!samePerson) setAppUser(null); })
        .finally(() => setIsLoading(false));
    } else {
      setAppUser(null);
      setIsLoading(false);
    }
  }, [session, sessionPending]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setAppUser(null);
    queryClient.clear();
    const result = await signIn.email({ email, password });
    if (result.error) {
      const msg = result.error.message ?? 'Login failed. Please try again.';
      setError(msg);
      throw new Error(msg);
    }
    try {
      const user = await api.getProfile();
      setAppUser(user);
    } catch {
      // session hook will sync the user on next render
    }
  }, [queryClient]);

  const register = useCallback(async (email: string, password: string, name?: string) => {
    setError(null);
    setAppUser(null);
    queryClient.clear();
    const result = await signUp.email({ email, password, name: name ?? '' });
    if (result.error) {
      const msg = result.error.message ?? 'Registration failed. Please try again.';
      setError(msg);
      throw new Error(msg);
    }
    try {
      const user = await api.getProfile();
      setAppUser(user);
    } catch {
      // session hook will sync the user on next render
    }
  }, [queryClient]);

  const logout = useCallback(async () => {
    queryClient.clear();
    await signOut();
    setAppUser(null);
    setError(null);
  }, [queryClient]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Better Auth uses cookies — no access token to expose
  const getAccessToken = useCallback(async () => null, []);

  const refreshUser = useCallback(async () => {
    try {
      const user = await api.getProfile();
      setAppUser(user);
    } catch {
      // Keep the current user; the session-change sync will reconcile later.
    }
  }, []);

  const setListPageSize = useCallback((listKey: string, size: number) => {
    // Optimistically update the in-memory user so the choice sticks across
    // navigation this session; persist to the server (reconciles on next /me).
    setAppUser((u) =>
      u
        ? { ...u, ui_preferences: { ...u.ui_preferences, pageSizes: { ...(u.ui_preferences?.pageSizes ?? {}), [listKey]: size } } }
        : u,
    );
    api.updateUiPreferences({ pageSizes: { [listKey]: size } }).catch(() => {
      /* non-fatal: preference will reconcile from the server on next load */
    });
  }, []);

  const value: AuthContextType = {
    user: appUser,
    isAuthenticated: !!appUser,
    isLoading: sessionPending || isLoading,
    error,
    login,
    register,
    logout,
    clearError,
    getAccessToken,
    setListPageSize,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
